// components/Enrollment/StudentMyRequests.js
// Student's enrollment request history
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { BookOpen, Clock, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';
import { AppBadge, EmptyState } from '../ui';
import { getMyRequests } from '../../utils/enrollmentApi';
import './_enrollment-pages.scss';

const statusConfig = {
  pending: { color: 'warning', icon: Clock, label: 'Pending' },
  approved: { color: 'success', icon: CheckCircle2, label: 'Approved' },
  paid: { color: 'success', icon: CheckCircle2, label: 'Paid & Enrolled' },
  rejected: { color: 'danger', icon: XCircle, label: 'Rejected' },
  expired: { color: 'secondary', icon: AlertCircle, label: 'Expired' },
};

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

export default function StudentMyRequests() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await getMyRequests();
        setRequests(res.data?.data || []);
      } catch {
        setRequests([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="enr-catalog__loading"><Spinner animation="border" /></div>;
  }

  return (
    <div className="enr-requests">
      <div className="enr-requests__header">
        <h2>My Applications</h2>
        <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Track your course enrollment requests</p>
      </div>

      {requests.length === 0 ? (
        <EmptyState
          icon={<BookOpen size={48} />}
          title="No applications yet"
          description="Browse available courses and apply to join a batch."
          actionLabel="Browse Courses"
          onAction={() => navigate('/s/courses')}
        />
      ) : (
        <div className="enr-requests__list">
          {requests.map(req => {
            const course = req.courseId || {};
            const batch = req.batchId || {};
            const cfg = statusConfig[req.status] || statusConfig.pending;
            const StatusIcon = cfg.icon;

            return (
              <div key={req._id} className="enr-requests__item">
                <div className="enr-requests__thumb">
                  {(course.thumbnail || course.image) ? (
                    <img src={course.thumbnail || course.image} alt={course.name} />
                  ) : (
                    <BookOpen size={24} />
                  )}
                </div>

                <div className="enr-requests__info">
                  <h4>{course.name || course.title || 'Course'}</h4>
                  <p>
                    {batch.name && <>Batch: {batch.name} &middot; </>}
                    Applied {formatDate(req.requestedAt || req.createdAt)}
                    {req.amountPaid > 0 && <> &middot; ₹{req.amountPaid.toLocaleString('en-IN')}</>}
                  </p>
                  {req.status === 'rejected' && req.rejectReason && (
                    <p style={{ color: 'var(--red-600, #dc2626)', fontSize: '0.8rem' }}>Reason: {req.rejectReason}</p>
                  )}
                </div>

                <div className="enr-requests__status">
                  <AppBadge color={cfg.color} size="sm">
                    <StatusIcon size={13} style={{ marginRight: 4 }} />
                    {cfg.label}
                  </AppBadge>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
